import { useHistory, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import queryString from "query-string";
import React, { useMemo } from "react";

import { ContainedButton } from "../../components/styled/Buttons";
import { filter } from "../../components/styled/filter/Filter.types";
import { getFilterParams, filtersSettingsComplete, filterIsReset } from "../../utils/filters";
import { Info } from "../../components/CapableIcons";
import { StyledWideTooltip } from "../../components/styled/Tooltips";
import { theme } from "../../styles/theme";
import Table from "../../components/styled/table/Table";
import PageHeader from "./PageHeader";

const ListView = ({
  createUrl,
  title,
  subtitle,
  createOnClick,
  createButtonLabel,
  disableRowClick,
  rows,
  tableColumns,
  page,
  path,
  totalRowCount,
  tooltipBody,
  nextParams,
  prevParams,
  useCursorPagination,
  openUrlInNewTab,
  filters,
  setFilters,
  globalOperator,
  setGlobalOperator,
  filterFieldOptions,
  setFilterSettings,
  sortingSettings,
  setSortingSettings,
  goToBetaRoute,
  rowClickUrl,
  onRowClick,
  onSearchChange,
  searchValue,
  customSearch,
  headerActions,
  subHeader,
  onSortClick,
  dense,
  fillHeight,
  paginationSummary,
}: {
  createUrl?: string;
  title: string;
  subtitle?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createOnClick?: any;
  createButtonLabel?: string;
  disableRowClick?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tableColumns: any;
  page: number;
  path: string;
  totalRowCount: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tooltipBody?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nextParams?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prevParams?: any;
  useCursorPagination?: boolean;
  openUrlInNewTab?: boolean;
  filters?: filter[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFilters?: any;
  globalOperator?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setGlobalOperator?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterFieldOptions?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  applyFilters?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFilterSettings?: any;
  sortingSettings?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSortingSettings?: any;
  goToBetaRoute?: (params) => boolean;
  rowClickUrl?: (any) => string;
  // Handles the click in place (e.g. opens a drawer) instead of navigating.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowClick?: (row: any) => void;
  onSearchChange?: (value: string) => void;
  searchValue?: string;
  customSearch?: React.ReactElement | null;
  headerActions?: React.ReactNode;
  subHeader?: React.ReactNode;
  onSortClick?: (columnId: string, additive: boolean) => void;
  dense?: boolean;
  // The page doesn't scroll: header and subHeader stay put, rows scroll in a box, pagination underneath.
  fillHeight?: boolean;
  paginationSummary?: React.ReactNode;
}): JSX.Element => {
  const history = useHistory();
  const { search } = useLocation();

  const queryParams = useMemo(() => {
    return queryString.parse(search);
  }, [search]);

  const applyFilters = () => {
    const resetURL = `${path}?page=1`;
    if (filtersSettingsComplete(filters)) {
      // all filter queries should use page 1 as default as we don't know how many results
      // there will be
      history.push(resetURL);
      setFilterSettings(getFilterParams(filters, globalOperator, filterFieldOptions));
    }
    if (filterIsReset(filters)) {
      history.push(resetURL);
      setFilterSettings(null);
    }
  };

  return (
    <Box
      sx={
        fillHeight
          ? {
              // PortalLayout's top nav is 72px (theme.spacing(9)).
              height: `calc(100vh - ${theme.spacing(9)})`,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }
          : { height: "100%", width: "100%" }
      }
    >
      {/* Title and actions go up into the top bar (see PageHeader). */}
      <PageHeader
        title={title}
        subtitle={subtitle}
        titleAdornment={
          tooltipBody && (
            <StyledWideTooltip title={tooltipBody} placement="bottom-start" arrow>
              <Box>
                <Info />
              </Box>
            </StyledWideTooltip>
          )
        }
        actions={
          (headerActions || createUrl || createOnClick) && (
            <>
              {headerActions}
              {createUrl ? (
                <ContainedButton
                  component={Link}
                  href={createUrl}
                  target={openUrlInNewTab ? "_blank" : "_self"}
                  data-sentry-unmask
                >
                  {createButtonLabel}
                </ContainedButton>
              ) : (
                createOnClick && (
                  <ContainedButton onClick={createOnClick} data-sentry-unmask>
                    {createButtonLabel}
                  </ContainedButton>
                )
              )}
            </>
          )
        }
      />
      <Box sx={{ height: (theme) => theme.spacing(1.5), flexShrink: 0 }} />
      {subHeader && (
        <Box sx={{ padding: (theme) => theme.spacing(0, 2), flexShrink: 0 }}>
          {subHeader}
        </Box>
      )}
      <Box
        sx={{
          cursor: disableRowClick ? "" : "pointer",
          ...(fillHeight ? { flex: 1, minHeight: 0, px: 2, pt: 1.5, pb: 1 } : {}),
        }}
      >
        <Table
          rows={rows}
          columns={tableColumns}
          pageIndex={page - 1}
          pageSize={10}
          path={path}
          onRowClick={(params) => {
            if (onRowClick && !disableRowClick) {
              onRowClick(params);
            } else if (path && params.id && !disableRowClick) {
              if (goToBetaRoute && goToBetaRoute(params)) {
                history.push(path + "/" + params.id + "/beta");
              } else {
                if (rowClickUrl) {
                  history.push(rowClickUrl(params));
                } else {
                  history.push(path + "/" + params.id + "/");
                }
              }
            }
          }}
          onPageChange={(pageIndex) => {
            if (path) {
              const newQueryParams = { ...queryParams, page: pageIndex + 1 };
              history.replace({
                pathname: path,
                search: "?" + queryString.stringify(newQueryParams),
              });
            }
          }}
          totalCount={totalRowCount}
          nextParams={nextParams}
          prevParams={prevParams}
          useCursorPagination={useCursorPagination}
          filters={filters}
          setFilters={setFilters}
          globalOperator={globalOperator}
          setGlobalOperator={setGlobalOperator}
          filterFieldOptions={filterFieldOptions}
          applyFilters={applyFilters}
          sortingSettings={sortingSettings}
          setSortingSettings={setSortingSettings}
          onSearchChange={onSearchChange}
          searchValue={searchValue}
          customSearch={customSearch}
          onSortClick={onSortClick}
          dense={dense}
          fillHeight={fillHeight}
          paginationSummary={paginationSummary}
        />
      </Box>
    </Box>
  );
};

export default ListView;
